// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import Checkbox, { CheckboxPresentationProps, StyleProps as CheckboxStyleProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImagePresentationProps, LazyImageProps } from "@insite/mobius/LazyImage";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getDigitalLineCardData from "@mcmc/App/Handlers/DigitalLineCardData/GetDigitalLineCardData";
import { DigitalLineCardDataCategory } from "@mcmc/App/Services/DigitalLineCardService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { screen } from "@mcmc/XDL/Helpers";
import { color, mergeWithCss } from "@mcmc/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    categoryId = "categoryId",
}

interface DigitalLineCardStyle {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    rightWrapper?: InjectableCss;
    brandsWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    filtersColumn?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    selectedAccordionSection?: AccordionSectionPresentationProps;
    radioGroup?: RadioGroupProps;
    radioButton?: RadioProps;
    brandWrapper?: ClickablePresentationProps;
    brandLogo?: LazyImageProps;
}

const sharedAccordionHeaderStyles: AccordionSectionHeaderProps = {
    css: css`
        button,
        button:focus {
            padding: 12px 24px !important;
            outline: none !important;
            border-top: none !important;
            border-left: none !important;
            border-right: none !important;
            border-bottom: 1px solid ${color("common.border")} !important;
            ${IconWrapper} {
                outline: none !important;
            }
            & span {
                font-size: 16px;
                font-weight: bold;
                line-height: 19px;
                letter-spacing: 0;
                text-transform: uppercase;
            }
        }
    `,
};

const styles: DigitalLineCardStyle = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            display: grid;
            grid-template-columns: 292px minmax(0, 1fr);
            column-gap: 32px;
            row-gap: 16px;

            &.single-category {
                grid-template-columns: minmax(0, 1fr);
            }

            ${screen("lte", "sm")`grid-template-columns: 1fr;`}
        `,
    },
    filtersColumn: {
        css: css`
            grid-column: 1 / 2;
            ${screen("lte", "sm")`width: 100%;`}
        `,
    },
    accordionSection: {
        mergeCss: true,
        headerProps: { ...sharedAccordionHeaderStyles },
    },
    selectedAccordionSection: {
        mergeCss: true,
        headerProps: {
            ...mergeWithCss(sharedAccordionHeaderStyles, {
                css: css`
                & span {
                        color ${color("primary")};
                    }
                `,
            }),
        },
    },
    radioGroup: {
        css: css`
            width: 100%;
        `,
    },
    radioButton: {
        css: css`
            display: flex;
            cursor: pointer;
            input[type="radio"] {
                margin-top: 0;
                & + label {
                    margin-top: 0;
                    cursor: pointer;
                }
            }
            margin-bottom: 8px;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },

    rightWrapper: {
        css: css`
            grid-column: 2 / 3;
            display: flex;
            flex-direction: column;
            gap: 30px;

            &.single-category {
                grid-column: 1 / 2;
            }

            ${screen("lte", "sm")`grid-column: 1 / 2;width: 100%;justify-content: center;`}
        `,
    },
    brandsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 28px;
            ${screen("lte", "sm")`width: 100%;justify-content: center;`}
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 20px;
        `,
    },
    title: {
        variant: "h1",
        css: css`
            letter-spacing: 0.4px;
            line-height: 36px;
            margin-bottom: 0;
        `,
    },
    brandWrapper: {
        css: css`
            width: 190px;
            height: 127px;
        `,
    },
    brandLogo: {
        width: "190px",
        height: "127px",
        css: css`
            width: 100%;
            height: 100%;
            filter: grayscale(1);
            &:hover {
                filter: grayscale(0);
            }
        `,
    },
};

const mapStateToProps = (state: McMcApplicationState, ownProps: WidgetProps) => {
    const dataViews = state.xDigitalLineCard.dataViews?.[ownProps.id];
    const parameter = state.xDigitalLineCard.parameter[ownProps.id];
    const isLoading = state.xDigitalLineCard.isLoading[ownProps.id];
    const categories = ownProps.fields.categoryId
        ? dataViews?.[ownProps.fields.categoryId]?.categories
        : dataViews?.["all"]?.categories;
    return {
        dataViews,
        isLoading,
        categories,
        parameter,
    };
};

const mapDispatchToProps = {
    getDigitalLineCardData,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface DigitalLineCardProps extends Props {
    fields: {
        [fields.title]: string;
        [fields.categoryId]: string;
    };
}

const DigitalLineCard = ({
    id,
    fields: { categoryId, title },
    dataViews,
    isLoading,
    parameter,
    categories,
    getDigitalLineCardData,
}: DigitalLineCardProps) => {
    useEffect(() => {
        if (categories) {
            return;
        }
        if (categoryId) {
            getDigitalLineCardData({ widgetId: id, categoryId });
            return;
        }
        getDigitalLineCardData({ widgetId: id, categoryId: "all" });
    }, [categoryId]);

    const updateCartegorySelection = (categoryId: string) => {
        getDigitalLineCardData({ categoryId, widgetId: id });
    };

    if (!dataViews && !isLoading) {
        return null;
    }

    if (isLoading && !dataViews) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    const data = dataViews?.[parameter];

    const isInitiallyExpanded = (category: DigitalLineCardDataCategory, selectedId?: string) => {
        if (!categoryId && !selectedId) {
            return false;
        }

        if (selectedId) {
            return category.id === selectedId || category.subCategories?.some(c => c.id === selectedId);
        }
        return category.subCategories?.some(c => c.id === categoryId);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.filtersColumn}>
                <Accordion headingLevel={2}>
                    {!categoryId && (
                        <ManagedAccordionSection
                            key="all"
                            title={translate("All")}
                            onTogglePanel={() => updateCartegorySelection("all")}
                            initialExpanded={false}
                            {...(parameter === "all" ? styles.selectedAccordionSection : styles.accordionSection)}
                        />
                    )}
                    {categories?.map(category => {
                        const initiallyExpanded = isInitiallyExpanded(category);
                        const isOpen = isInitiallyExpanded(category, parameter);
                        return (
                            <ManagedAccordionSection
                                key={category.id}
                                title={category.name}
                                onTogglePanel={() => updateCartegorySelection(category.id)}
                                initialExpanded={initiallyExpanded}
                                {...{ isOpen, hideToggler: categoryId && isOpen }}
                                {...(parameter === category.id
                                    ? styles.selectedAccordionSection
                                    : styles.accordionSection)}
                            >
                                {category.subCategories?.map(sCategory => (
                                    <RadioGroup
                                        key={sCategory.id}
                                        value={parameter}
                                        onChange={() => updateCartegorySelection(sCategory.id)}
                                        {...styles.radioGroup}
                                    >
                                        <Radio key={sCategory.id} value={sCategory.id} {...styles.radioButton}>
                                            {sCategory.name}
                                        </Radio>
                                    </RadioGroup>
                                ))}
                            </ManagedAccordionSection>
                        );
                    })}
                </Accordion>
            </StyledWrapper>

            <StyledWrapper {...styles.rightWrapper}>
                {!!title && (
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography aria-label={title} {...styles.title}>
                            {title}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.brandsWrapper}>
                    {data?.brands.map(brand => {
                        return (
                            <Clickable
                                key={brand.id}
                                href={brand.siteUrl}
                                {...styles.brandWrapper}
                                title={brand.name}
                                target="_blank"
                            >
                                <LazyImage src={brand.logoImagePath} altText={brand.name} {...styles.brandLogo} />
                            </Clickable>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DigitalLineCard),
    definition: {
        group: "Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                defaultValue: "Line Card",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                isRequired: false,
            },
            {
                name: fields.categoryId,
                displayName: "Category",
                defaultValue: "",
                fieldType: "Contextual",
                editorTemplate: "CustomCategoriesField",
            } as any,
        ],
    },
};

export default widgetModule;
